import { MedicalImageViewOptions } from '@/shared/api/protocol-ts/model/dto_common_image_view_options_pb';
import { Annotation } from '@/shared/graphics/RenderComponents/AnnotationsLayer/Annotation';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { assetsModel, formatAnnotations } from '@/entities/assets';
import { reportsModel, useCheckReportSignature } from '@/entities/reports';
import { toothModel } from '@/entities/tooth';

export const useImagePropsSetters = (assetID: string, toothID?: string) => {
  const { checkReportSignature } = useCheckReportSignature();

  const dispatch = useAppDispatch();

  const tooth = useAppSelector((state) =>
    toothModel.selectors.selectById(state, toothID ?? ''),
  );
  const isReportSigned = useAppSelector(
    reportsModel.selectors.selectCurrentReportSigned,
  );

  const handleSetAnnotations = async (changedAnnotations: Annotation[]) => {
    if (!assetID) {
      return;
    }

    checkReportSignature({
      toothID,
      onSignatureChecked: async () => {
        // NOTE: In the checkReportSignature function, tooth disapproval only occurs when the report is signed.
        // However, in handleSetAnnotations, the tooth gets disapproved regardless of report signature status.
        // This, combined with the closure of the tooth?.IsApproved check in the case of a signed report,
        // leads to the tooth being disapproved twice.
        // Therefore, we need to add a !isReportSigned check to prevent this double disapproval.
        if (tooth?.IsApproved && !isReportSigned) {
          await dispatch(
            reportsModel.thunks.setReportToothDisapproved({
              ToothID: tooth.ID,
            }),
          );
        }

        await dispatch(
          assetsModel.thunks.setTopLayerAnnotations({
            AssetID: assetID,
            Annotations: formatAnnotations(changedAnnotations),
          }),
        );
      },
      onCancel: () => {},
    });
  };

  const handleSetMedicalImageViewOptions = async (
    viewOption: MedicalImageViewOptions,
  ) => {
    if (!assetID) {
      return;
    }

    await dispatch(
      assetsModel.thunks.setMedicalImageViewOptions({
        AssetID: assetID,
        MedicalImageViewOptionsV2: viewOption,
      }),
    );
  };

  const handleSetReportMedicalImageViewOptions = async (
    viewOption: Partial<MedicalImageViewOptions>,
    reportID: string,
  ) => {
    await dispatch(
      reportsModel.thunks.setReportMedicalImageViewOptions({
        ReportID: reportID,
        MedicalImageViewOptionsV2: viewOption as MedicalImageViewOptions,
      }),
    );
  };

  return {
    handleSetAnnotations,
    handleSetMedicalImageViewOptions,
    handleSetReportMedicalImageViewOptions,
  };
};
